import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import Layout from '@/components/Layout';
import { ROUTE_PATHS } from '@/lib/index';

const MILESTONES = [
  { year: '1977', text: '坦桑尼亚航空成立，开始国内运营' },
  { year: '2016', text: '重新注册为 Air Tanzania Company Ltd，政府全资控股' },
  { year: '2017', text: '接收首架波音787-8梦想客机，开启长途国际航线' },
  { year: '2018', text: '开通广州直飞航线，成为中坦航空门户' },
  { year: '2019', text: '机队扩张至10架，国内航线覆盖全国11城市' },
  { year: '2022', text: '引入波音737 MAX 9，优化中短途航线效率' },
  { year: '2024', text: '通过IOSA国际航空运输安全审核认证' },
  { year: '2025', text: '开通塞舌尔新航线，机队规模达到16架' },
];

const STATS = [
  { v: '1977', l: '成立年份', icon: '✈️' },
  { v: '16架', l: '现役机队', icon: '🛩️' },
  { v: '30+', l: '目的地城市', icon: '🌍' },
  { v: '3班/周', l: '广州直飞', icon: '📍' },
  { v: 'IOSA', l: '安全认证', icon: '🏆' },
  { v: '100%', l: '政府控股', icon: '🇹🇿' },
];

export default function About() {
  return (
    <Layout>
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[440px] overflow-hidden">
        <img src="/images/57x42cm(Dreamliner).jpg" alt="坦桑尼亚航空"
          className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/50 to-secondary/90" />
        <div className="absolute inset-0 flex flex-col justify-end pb-12">
          <div className="max-w-7xl mx-auto px-6 w-full">
            <p className="text-accent text-xs uppercase tracking-widest font-bold mb-2">The Wings of Kilimanjaro</p>
            <h1 className="text-5xl font-bold text-white mb-1">关于我们</h1>
            <p className="text-white/65">坦桑尼亚国家航空公司 · 连接中国与东非</p>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="bg-secondary text-white py-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {STATS.map(s => (
              <div key={s.l} className="text-center">
                <div className="text-2xl mb-1">{s.icon}</div>
                <div className="text-2xl font-bold text-accent">{s.v}</div>
                <div className="text-xs text-white/50 mt-0.5">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand story: split layout */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div className="order-2 lg:order-1">
              <h2 className="text-3xl font-bold mb-4">乞力马扎罗之翼</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                坦桑尼亚航空（Air Tanzania Company Limited, ATCL）是坦桑尼亚的国家航空公司，
                总部位于达累斯萨拉姆，由坦桑尼亚联合共和国政府全资拥有。
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                以"乞力马扎罗之翼"为品牌愿景，航空公司的标志为傲然的长颈鹿，代表坦桑尼亚独特的
                野生动物和大地之美。公司以安全、舒适、准时为核心服务承诺，
                通过先进的波音787-8梦想客机将中国与东非大陆直接相连。
              </p>
              <p className="text-muted-foreground leading-relaxed mb-6">
                自2018年开通广州直飞以来，坦桑尼亚航空已成为中坦两国人文交流、
                商务往来和旅游探索的首选航空纽带，每周三班为旅客提供最便捷的直飞服务。
              </p>
              <Link to={ROUTE_PATHS.ROUTES}
                className="inline-flex items-center gap-2 px-6 py-3 bg-secondary text-white rounded-xl font-semibold text-sm hover:bg-secondary/90 transition">
                查看航线 <ArrowRight size={14} />
              </Link>
            </div>
            <div className="order-1 lg:order-2 relative">
              <img src="/images/57x42cm(Cabin Crew).jpg" alt="坦桑尼亚航空机组"
                className="rounded-2xl w-full object-cover h-80" />
              <img src="/images/25x21.5cm (fun photo).jpg" alt="团队照片"
                className="absolute -bottom-5 -left-5 w-40 h-28 object-cover rounded-xl border-4 border-background shadow-xl hidden md:block" />
            </div>
          </div>

          {/* Photos row */}
          <div className="grid grid-cols-3 gap-3 mb-16">
            {[
              { img: '/images/57x42cm(Pilot).jpg',       cap: '飞行团队',     pos: 'object-[center_25%]' },
              { img: '/images/experience-about.jpg',       cap: '空中服务',     pos: 'object-[center_20%]' },
              { img: '/images/fun-photo-about.jpg',        cap: '专业地面团队', pos: 'object-[center_top]' },
            ].map(item => (
              <div key={item.cap} className="relative rounded-2xl overflow-hidden h-64 group">
                <img src={item.img} alt={item.cap}
                  className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${(item as any).pos || ''}`} />
                <div className="absolute inset-0 bg-gradient-to-t from-secondary/70 to-transparent" />
                <span className="absolute bottom-3 left-3 text-white text-sm font-semibold">{item.cap}</span>
              </div>
            ))}
          </div>

          {/* Timeline */}
          <h2 className="text-2xl font-bold mb-8 text-center">发展历程</h2>
          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-border -ml-px" />
            <div className="space-y-8">
              {MILESTONES.map((m, i) => (
                <motion.div key={m.year} initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className={`relative flex ${i % 2 === 0 ? 'md:justify-start' : 'md:justify-end'} pl-10 md:pl-0`}>
                  <div className={`w-full md:w-5/12 bg-card border border-border rounded-2xl p-4 ${i % 2 === 0 ? 'md:mr-8' : 'md:ml-8'}`}>
                    <span className="text-primary font-bold font-mono text-sm">{m.year}</span>
                    <p className="text-muted-foreground text-sm mt-0.5">{m.text}</p>
                  </div>
                  <div className="absolute left-0 md:left-1/2 w-3 h-3 rounded-full bg-primary border-2 border-background -mt-1 top-5 md:-ml-1.5" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* IOSA & Certification */}
      <section className="py-12 bg-muted">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-card rounded-2xl p-6 border border-border">
              <div className="text-3xl mb-3">🏆</div>
              <h3 className="font-bold mb-2">IOSA 国际安全认证</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                坦桑尼亚航空已获得国际航空运输协会运营安全审计（IOSA）认证，
                这是全球航空业最权威的安全认证之一，代表飞行安全与运营标准达到国际水准。
              </p>
            </div>
            <div className="bg-card rounded-2xl p-6 border border-border">
              <div className="text-3xl mb-3">🤝</div>
              <h3 className="font-bold mb-2">成员资格</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                IATA国际航空运输协会成员、非洲航空公司协会（AFRAA）成员、
                东非航空（EAC）区域合作成员，与全球主要航空公司保持代码共享协议。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 bg-secondary text-white text-center">
        <h2 className="text-2xl font-bold mb-1">准备好飞往非洲了吗？</h2>
        <p className="text-white/55 text-sm mb-5">每周三班 · 广州直飞达累斯萨拉姆与桑给巴尔</p>
        <Link to={ROUTE_PATHS.BOOKING}
          className="inline-flex items-center gap-2 px-8 py-4 bg-accent text-accent-foreground rounded-xl font-bold hover:bg-accent/90 transition">
          立即订票
        </Link>
      </section>
    </Layout>
  );
}
